import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { Button } from "../../button";
import { TableCell, TableRow } from "../table";

export interface TableErrorStateProps {
  colSpan: number;
  title?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  retryLabel?: React.ReactNode;
  onRetry?: () => void;
  className?: string;
  cellClassName?: string;
  showRetryButton?: boolean;
}

export function TableErrorState({
  colSpan,
  title,
  description,
  error,
  retryLabel,
  onRetry,
  className,
  cellClassName,
  showRetryButton = true,
}: TableErrorStateProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("ui.something_went_wrong");
  const resolvedDescription = description ?? t("ui.error_loading_data");
  const resolvedRetryLabel = retryLabel ?? t("ui.try_again");
  const hasError = error !== null && error !== undefined && error !== false && !(typeof error === "string" && error.trim() === "");

  return (
    <TableRow className={cn("border-b-0 hover:bg-transparent", className)}>
      <TableCell colSpan={colSpan} className={cn("px-0 py-10", cellClassName)}>
        <div className="flex min-h-[220px] w-full items-center justify-center px-4">
          <div className="flex w-full max-w-none flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-6 py-8 text-center shadow-sm dark:border-red-900/40 dark:bg-red-950/20">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="h-6 w-6 text-red-500 dark:text-red-300" />
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {resolvedTitle}
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {resolvedDescription}
            </div>
            {hasError && (
              <div className="mt-3 rounded-md bg-white px-3 py-2 text-xs text-red-600 shadow-sm dark:bg-gray-950 dark:text-red-300">
                {error}
              </div>
            )}
            {showRetryButton && onRetry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="mt-4 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {resolvedRetryLabel}
              </Button>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}
